import React, { useEffect } from "react";
import { useIsAdmin } from "@/shared/hooks/useIsAdmin";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import Table from "@/shared/components/ui/table";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  MoreHorizontal,
  Loader2,
  AlertCircle,
  RotateCcw,
  FileText,
  Plus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/shared/components/ui/dropdown-menu";
import { DocumentType, DocumentMetadata } from "@/shared/types/documentTypes";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { replaceBucketContent } from "@/shared/utils/templateReplacement";
import { useDocumentDelete, useDocumentRename } from "@/shared/hooks/useDocumentStorage";
import { downloadSingleDocument } from "@/shared/utils/downloadDocuments";
import { useAppContext } from "@/shared/hooks/useAppContext";
import useBreakpoints from "@/shared/hooks/useBreakpoints";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { enqueueSnackbar } from "notistack";
import { Tag } from "@/shared/components/ui/tag";
import { supabase } from "@/shared/lib/supabase";
import { sentryUtils } from "@/shared/utils/sentry";
import logoIcon from "@/shared/assets/logo/logo-icon.svg";

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M16.6667 16.6667C17.1087 16.6667 17.5326 16.4911 17.8452 16.1785C18.1577 15.866 18.3333 15.442 18.3333 15V6.66667C18.3333 6.22464 18.1577 5.80072 17.8452 5.48816C17.5326 5.17559 17.1087 5 16.6667 5H10.0833C9.80459 5.00273 9.52962 4.93551 9.28358 4.80448C9.03755 4.67346 8.8283 4.48281 8.675 4.25L8 3.25C7.84824 3.01956 7.64165 2.8304 7.39875 2.6995C7.15585 2.56859 6.88426 2.50005 6.60833 2.5H3.33333C2.89131 2.5 2.46738 2.67559 2.15482 2.98816C1.84226 3.30072 1.66667 3.72464 1.66667 4.16667V15C1.66667 15.442 1.84226 15.866 2.15482 16.1785C2.46738 16.4911 2.89131 16.6667 3.33333 16.6667H16.6667Z"
        fill="#A6A09B"
        stroke="#A6A09B"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface DocumentRow {
  id: number;
  name: React.ReactNode;
  type: React.ReactNode;
  uploadDate: React.ReactNode;
  uploadedBy: React.ReactNode;
  actions: React.ReactNode;
}

type DocumentWithMeta = DocumentMetadata & {
  uploaded_by_user_id: number;
  uploaded_by_name: string;
  display_name: string | null;
};

import { useMoveDocument } from "@/shared/hooks/useDocumentFolders";

import type { DocumentFolder } from "@/shared/types/folderTypes";

interface DocumentVaultTableProps {
  documents: DocumentWithMeta[];
  allDocuments?: DocumentWithMeta[];
  isLoading: boolean;
  error?: Error | null;
  onUploadClick?: () => boolean | void;
  documentTypes: { id: DocumentType; label: string }[];
  hasSearchOrFilter?: boolean;
  onClearFilters?: () => void;
  folders?: DocumentFolder[];
  onFolderOpen?: (folderId: number) => void;
  onFolderRename?: (folder: DocumentFolder) => void;
  onFolderDelete?: (folder: DocumentFolder) => void;
  onFolderDownload?: (folder: DocumentFolder) => void;
  onNewFolderAndMove?: (documentId: number) => void;
  insideFolder?: boolean;
  allFolders?: DocumentFolder[];
}

// Helper to format a person's name as "FirstName L." (first name + last initial)
function formatNameWithInitial(fullName: string): string {
  if (!fullName) return "";

  const nameParts = fullName.trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts[nameParts.length - 1] || "";
  const lastInitial =
    lastName && nameParts.length > 1 ? `${lastName.charAt(0).toUpperCase()}.` : "";

  return `${firstName} ${lastInitial}`.trim();
}

// Helper to format TPA document type with user's first name and last initial
function formatTPADocumentType(uploaderName: string): string {
  if (!uploaderName) return "Third-Party Authorization";
  return `Third-Party Authorization ${formatNameWithInitial(uploaderName)}`.trim();
}

// Helper to format decedent ID document type with decedent's first name
function formatDecedentIDType(decedentName?: string): string {
  if (!decedentName) return "Decedent's Photo ID";
  const firstName = decedentName.trim().split(/\s+/)[0] || "";
  return `${firstName}'s Photo ID`;
}

// Document type display configuration
interface DocumentDisplayConfig {
  matches: string[];
  label: string;
  includeDecedent?: boolean;
  includeUploader?: boolean;
}

const DOCUMENT_DISPLAY_CONFIGS: DocumentDisplayConfig[] = [
  // Decedent-related documents (include decedent name)
  {
    matches: ["death_certificate"],
    label: "Death Certificate",
    includeDecedent: true,
  },
  { matches: ["will"], label: "Will", includeDecedent: true },
  { matches: ["trust"], label: "Trust", includeDecedent: true },
  { matches: ["decedent_id"], label: "Photo ID", includeDecedent: true },
  {
    matches: ["insurance_document", "insurance_documents"],
    label: "Insurance Document",
    includeDecedent: true,
  },
  {
    matches: ["tax_document", "tax_documents"],
    label: "Tax Document",
    includeDecedent: true,
  },
  {
    matches: ["investment_account_statement", "investment_statements"],
    label: "Investment Account Statement",
    includeDecedent: true,
  },
  {
    matches: ["financial_property_documents"],
    label: "Financial / Property Documents",
    includeDecedent: true,
  },
  // Documents with smart naming from extraction (don't include decedent - server sets display_name)
  {
    matches: ["bank_statement", "bank_investment_statement"],
    label: "Bank Statement",
  },
  { matches: ["property_deed", "property_deeds"], label: "Property Deed" },
  { matches: ["vehicle_title", "vehicle_titles"], label: "Vehicle Title" },
  { matches: ["credit_report"], label: "Credit Report" },
  // Uploader-related documents (include uploader name)
  {
    matches: ["identification_card", "my_photo_id"],
    label: "Identification Card",
    includeUploader: true,
  },
  // Generated letters (include decedent name)
  {
    matches: ["generated_letter"],
    label: "Generated Letter",
    includeDecedent: true,
  },
  // Other/unknown (include decedent name)
  { matches: ["other"], label: "Other Document", includeDecedent: true },
  {
    matches: ["third_party_authorization"],
    label: "Third-Party Authorization",
    includeUploader: true,
  },
];

// Generate a human-readable display name for a document based on its type and metadata
function generateDocumentDisplayName(
  documentType: string,
  uploaderName: string,
  decedentName?: string,
): string {
  const config = DOCUMENT_DISPLAY_CONFIGS.find((c) =>
    c.matches.some((m) => m.toLowerCase() === documentType.toLowerCase()),
  );

  if (config) {
    const { label, includeDecedent, includeUploader } = config;
    if (includeDecedent && decedentName) {
      return `${label} - ${formatNameWithInitial(decedentName)}`;
    }
    if (includeUploader && uploaderName) {
      return `${label} - ${formatNameWithInitial(uploaderName)}`;
    }
    return label;
  }

  // Fallback: format the document type nicely
  const formattedType = documentType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  if (decedentName) {
    return `${formattedType} - ${formatNameWithInitial(decedentName)}`;
  }
  return formattedType;
}

function ElayneTeamBadge() {
  return (
    <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-medium bg-green-100 text-brand border border-green-300">
      <img src={logoIcon} alt="" aria-hidden="true" className="h-3 w-3 shrink-0" />
      <span>Elayne Team</span>
    </div>
  );
}

function DocumentUploadedBy({
  name,
  isConfirmation,
  recipientName,
}: {
  name: string;
  isConfirmation?: boolean;
  recipientName?: string | null;
}) {
  if (isConfirmation || name === "Elayne Team" || name === "Elayne") {
    return <ElayneTeamBadge />;
  }

  if (recipientName) {
    return <span className="text-sm text-gray-700">{recipientName} (via link)</span>;
  }

  return <span className="text-sm text-gray-700">{name}</span>;
}

const DocumentVaultTable: React.FC<DocumentVaultTableProps> = ({
  documents,
  allDocuments,
  isLoading,
  error: _error, // eslint-disable-line @typescript-eslint/no-unused-vars
  onUploadClick,
  hasSearchOrFilter = false,
  onClearFilters,
  folders = [],
  onFolderOpen,
  onFolderRename,
  onFolderDelete,
  onFolderDownload,
  onNewFolderAndMove,
  insideFolder = false,
  allFolders = [],
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deceasedUserData = useDeceasedUserData();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [documentToDelete, setDocumentToDelete] = React.useState<{
    id: number;
    fileName: string;
  } | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [documentToReset, setDocumentToReset] = React.useState<{
    id: number;
    fileName: string;
  } | null>(null);
  const [isResetting, setIsResetting] = React.useState(false);
  const { isAdmin } = useIsAdmin();

  const deleteMutation = useDocumentDelete();
  const renameMutation = useDocumentRename();
  const { person } = useAppContext();
  const { isMobile } = useBreakpoints();
  const [mobileMoveDoc, setMobileMoveDoc] = React.useState<{ id: number; folderId: number | null } | null>(null);
  const [documentSheetOpen, setDocumentSheetOpen] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<{
    id: number;
    name: string;
    createdAt: Date;
  } | null>(null);
  const [renamingDocumentId, setRenamingDocumentId] = React.useState<number | null>(null);
  const [newDocumentName, setNewDocumentName] = React.useState<string>("");
  const [loadingDocumentId, setLoadingDocumentId] = React.useState<number | null>(null);
  const moveDocMutation = useMoveDocument();
  const handleMoveToFolder = async (documentId: number, folderId: number | null) => {
    try {
      await moveDocMutation.mutateAsync({ documentId, folderId });
      const targetName = folderId
        ? allFolders.find((f) => f.id === folderId)?.name ?? "folder"
        : "main list";
      enqueueSnackbar(`Moved to ${targetName}`, { variant: "success", autoHideDuration: 3000 });
    } catch {
      enqueueSnackbar("Couldn't move document. Please try again.", {
        variant: "error", autoHideDuration: 5000,
      });
    }
  };

  // Sync document viewer with ?doc= query param
  useEffect(() => {
    const docId = searchParams.get("doc");

    // Close sheet if ?doc= was removed (e.g., browser back button)
    if (!docId && documentSheetOpen) {
      setDocumentSheetOpen(false);
      setLoadingDocumentId(null);
      return;
    }

    if (isLoading || !docId) return;

    const id = parseInt(docId, 10);
    if (isNaN(id)) return;

    // Skip if already viewing this document
    if (documentSheetOpen && selectedDocument?.id === id) return;

    const lookupDocs = allDocuments || documents;
    const doc = lookupDocs.find((d) => d.id === id);
    if (doc) {
      setSelectedDocument({
        id: doc.id,
        name: doc.display_name || doc.file_name,
        createdAt: new Date(doc.created_at),
      });
      setDocumentSheetOpen(true);
    }
  }, [isLoading, searchParams, allDocuments, documents, documentSheetOpen, selectedDocument?.id]);

  const handleUploadClick = () => {
    onUploadClick?.();
  };

  // Upload dialog is controlled by the parent component

  const handleDownloadDocument = async (document: DocumentMetadata) => {
    if (!person?.estate_id) return;
    try {
      await downloadSingleDocument(person.estate_id, document.id, document.display_name || document.file_name);
    } catch (err) {
      enqueueSnackbar("Couldn't download document. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
      sentryUtils.captureError(err, "Document download failed");
    }
  };

  const handleDeleteClick = (document: DocumentMetadata) => {
    setDocumentToDelete({
      id: document.id,
      fileName: document.file_name,
    });
    setDeleteDialogOpen(true);
  };

  const handleResetClick = (document: DocumentMetadata) => {
    setDocumentToReset({
      id: document.id,
      fileName: document.file_name,
    });
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!documentToReset) return;
    setIsResetting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/0/document/${documentToReset.id}/reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );
      if (!response.ok) throw new Error("Reset failed");
      const result = await response.json();
      enqueueSnackbar(
        `Reset: ${result.deleted.contacts} contacts, ${result.deleted.estateItems} items, ${result.deleted.beneficiaries} beneficiaries deleted.`,
        { variant: "success", autoHideDuration: 5000 },
      );
      setResetDialogOpen(false);
      setDocumentToReset(null);
    } catch (error) {
      enqueueSnackbar("Couldn't reset document data. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      sentryUtils.captureError(error, "Document reset failed");
    } finally {
      setIsResetting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      await deleteMutation.mutateAsync(documentToDelete.id);
      enqueueSnackbar("Document deleted", {
        variant: "success",
        autoHideDuration: 3000,
      });
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      enqueueSnackbar("Couldn't delete document. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      sentryUtils.captureError(error, "Document delete failed");
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleDocumentClick = (document: DocumentMetadata) => {
    setLoadingDocumentId(document.id);
    setSelectedDocument({
      id: document.id,
      name: document.display_name || document.file_name,
      createdAt: new Date(document.created_at),
    });
    setDocumentSheetOpen(true);
    const alreadyViewingDoc = searchParams.has("doc");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("doc", String(document.id));
        return next;
      },
      { replace: alreadyViewingDoc },
    );
  };

  const handleDocumentLoaded = () => {
    setLoadingDocumentId(null);
  };

  const handleRenameClick = (document: DocumentMetadata) => {
    // Remove file extension before setting initial name
    const nameWithoutExtension = document.file_name.replace(
      /\.(pdf|doc|docx|txt|jpg|jpeg|png|gif|bmp|tiff|svg)$/i,
      "",
    );
    setRenamingDocumentId(document.id);
    setNewDocumentName(nameWithoutExtension);
  };

  const handleRenameComplete = async (documentId: number) => {
    if (!newDocumentName.trim()) {
      enqueueSnackbar("Document name can't be empty", {
        variant: "error",
        autoHideDuration: 3000,
      });
      setRenamingDocumentId(null);
      return;
    }

    // Find the original document to get its extension
    const originalDocument = documents.find((doc) => doc.id === documentId);
    if (!originalDocument) {
      enqueueSnackbar("Document not found", {
        variant: "error",
        autoHideDuration: 3000,
      });
      setRenamingDocumentId(null);
      return;
    }

    // Extract the original file extension
    const extensionMatch = originalDocument.file_name.match(
      /\.(pdf|doc|docx|txt|jpg|jpeg|png|gif|bmp|tiff|svg)$/i,
    );
    const extension = extensionMatch ? extensionMatch[0] : "";

    // Append the extension to the new name
    const newFileNameWithExtension = newDocumentName.trim() + extension;

    try {
      await renameMutation.mutateAsync({
        documentId,
        newFileName: newFileNameWithExtension,
      });
      enqueueSnackbar("Document renamed", {
        variant: "success",
        autoHideDuration: 3000,
      });
      setRenamingDocumentId(null);
    } catch (error) {
      enqueueSnackbar("Couldn't rename document. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      sentryUtils.captureError(error, "Document rename failed");
    }
  };

  const handleRenameCancel = () => {
    setRenamingDocumentId(null);
    setNewDocumentName("");
  };

  const columns = insideFolder
    ? [
        { header: "Name", accessor: "name" as keyof DocumentRow, className: "w-[350px] max-w-[350px]" },
        { header: "Upload Date", accessor: "uploadDate" as keyof DocumentRow, className: "w-[150px]" },
        { header: "Uploaded by", accessor: "uploadedBy" as keyof DocumentRow, className: "w-[150px]" },
        { header: "", accessor: "actions" as keyof DocumentRow, className: "w-[60px]" },
      ]
    : [
        { header: "Name", accessor: "name" as keyof DocumentRow, className: "w-[350px] max-w-[350px]" },
        { header: "Type", accessor: "type" as keyof DocumentRow, className: "w-[200px]" },
        { header: "Upload Date", accessor: "uploadDate" as keyof DocumentRow, className: "w-[150px]" },
        { header: "Uploaded by", accessor: "uploadedBy" as keyof DocumentRow, className: "w-[150px]" },
        { header: "", accessor: "actions" as keyof DocumentRow, className: "w-[60px]" },
      ];

  const skeletonRows: DocumentRow[] = Array.from({ length: 9 }, (_, index) => ({
    id: index,
    name: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-48" />
      </div>
    ),
    type: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-24" />
      </div>
    ),
    uploadDate: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-20" />
      </div>
    ),
    uploadedBy: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-32" />
      </div>
    ),
    actions: (
      <div className="flex items-center h-full">
        <Skeleton className="h-4 w-4" />
      </div>
    ),
  }));

  // Helper to check if a document is being analyzed (type inference in progress)
  const isDocumentBeingAnalyzed = (doc: DocumentMetadata) => {
    return (
      doc.document_type === "other" &&
      (doc.extraction_status === "no_extraction" ||
        doc.extraction_status === "extraction_in_progress" ||
        (doc.extraction_status as string) === "pending" ||
        (doc.extraction_status as string) === "in_progress" ||
        (doc.extraction_status as string) === "processing")
    );
  };

  // Helper to get display name from database, with fallback for analyzing documents
  const getDocumentDisplayName = (
    doc: DocumentMetadata & {
      uploaded_by_name: string;
      display_name: string | null;
    },
  ) => {
    // While analyzing, show the original filename (without extension)
    if (isDocumentBeingAnalyzed(doc)) {
      return doc.file_name.replace(/\.(pdf|doc|docx|txt|jpg|jpeg|png|gif|bmp|tiff|svg)$/i, "");
    }

    // Use display_name from database, fallback to file_name if not set
    if (doc.display_name) {
      return doc.display_name.replace(/\.(pdf|doc|docx|txt|jpg|jpeg|png|gif|bmp|tiff|svg)$/i, "");
    }

    // Fallback: generate readable name (for old documents without display_name)
    return generateDocumentDisplayName(
      doc.document_type,
      doc.uploaded_by_name,
      deceasedUserData?.fullName,
    );
  };

  const documentRows: DocumentRow[] = documents.map((document) => ({
    id: document.id,
    name: (
      <div className="max-w-[350px] overflow-hidden">
        {renamingDocumentId === document.id ? (
          <div onClick={(e) => e.stopPropagation()}>
            <Input
              type="text"
              value={newDocumentName}
              onChange={(e) => setNewDocumentName(e.target.value)}
              onBlur={() => handleRenameComplete(document.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRenameComplete(document.id);
                } else if (e.key === "Escape") {
                  handleRenameCancel();
                }
              }}
              className="text-sm h-8 w-full max-w-[280px]"
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-stone-400 shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate" title={getDocumentDisplayName(document)}>
                {getDocumentDisplayName(document)}
              </span>
              {hasSearchOrFilter && document.folder_id && (() => {
                const folderName = allFolders.find((f) => f.id === document.folder_id)?.name;
                return folderName ? (
                  <span className="text-xs text-tertiary truncate">in {folderName}</span>
                ) : null;
              })()}
            </div>
            {document.duplicate_of_name && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Tag size="sm" color="orange" icon={AlertCircle}>
                        Duplicate Detected
                      </Tag>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="text-center">
                    This appears to be a duplicate of {document.duplicate_of_name}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {loadingDocumentId === document.id && (
              <Loader2 className="h-4 w-4 animate-spin text-teal-600 flex-shrink-0" />
            )}
          </div>
        )}
      </div>
    ),
    type:
      // Show "Analyzing..." for documents being analyzed (type inference in progress)
      isDocumentBeingAnalyzed(document) ? (
        <Tag size="sm" color="stone" icon={Loader2} iconClassName="animate-spin">
          Analyzing...
        </Tag>
      ) : (
        <Tag size="sm" color="stone">
          {document.is_confirmation
            ? `Confirmation Document - ${replaceBucketContent(getDocumentTypeLabel(document.document_type), deceasedUserData)}`
            : document.document_type === DocumentType.THIRD_PARTY_AUTHORIZATION
              ? formatTPADocumentType(document.uploaded_by_name)
              : document.document_type === DocumentType.DECEDENT_ID
                ? formatDecedentIDType(deceasedUserData?.fullName)
                : replaceBucketContent(
                    getDocumentTypeLabel(document.document_type),
                    deceasedUserData,
                  )}
        </Tag>
      ),
    uploadDate: document.created_at ? format(new Date(document.created_at), "MMM dd, yyyy") : "—",
    uploadedBy: (
      <DocumentUploadedBy
        name={document.uploaded_by_name}
        isConfirmation={document.is_confirmation}
        recipientName={document.recipient_name}
      />
    ),
    actions: (
      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-stone-100">
              <MoreHorizontal className="h-4 w-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" collisionPadding={16} className="min-w-[240px] p-2">
            <DropdownMenuItem
              onClick={() => handleDocumentClick(document)}
              className="cursor-pointer h-10 px-3"
            >
              Open
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDownloadDocument(document)} className="cursor-pointer h-10 px-3">
              Download
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleRenameClick(document)}
              className="cursor-pointer h-10 px-3"
            >
              Rename
            </DropdownMenuItem>
            {isMobile ? (
              <DropdownMenuItem
                onClick={() => setMobileMoveDoc({ id: document.id, folderId: document.folder_id ?? null })}
                className="cursor-pointer h-10 px-3"
              >
                Move to folder
              </DropdownMenuItem>
            ) : (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer h-10 px-3">
                  Move to folder
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent sideOffset={12} className="min-w-[260px] p-2">
                  <DropdownMenuLabel className="px-3 text-xs font-normal text-tertiary">Move to</DropdownMenuLabel>
                  {allFolders
                    .filter((f) => f.id !== (document.folder_id ?? undefined))
                    .map((folder) => (
                      <DropdownMenuItem
                        key={folder.id}
                        onClick={() => handleMoveToFolder(document.id, folder.id)}
                        className="cursor-pointer h-10 px-3"
                      >
                        {folder.name}
                      </DropdownMenuItem>
                    ))}
                  {document.folder_id && (
                    <DropdownMenuItem
                      onClick={() => handleMoveToFolder(document.id, null)}
                      className="cursor-pointer h-10 px-3"
                    >
                      Main list
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onNewFolderAndMove?.(document.id)}
                    className="cursor-pointer h-10 px-3 text-brand"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    New folder
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {isAdmin && (
              <DropdownMenuItem
                onClick={() => handleResetClick(document)}
                className="cursor-pointer h-10 px-3 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => handleDeleteClick(document)}
              className="cursor-pointer h-10 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ),
  }));

  const folderRows: DocumentRow[] = folders.map((folder) => {
    const typeLabel =
      folder.document_types.length === 1
        ? getDocumentTypeLabel(folder.document_types[0])
        : folder.document_types.length === 0
          ? ""
          : "Mixed";

    return {
      id: -folder.id,
      name: (
        <div className="flex items-center gap-2">
          <FolderIcon className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium truncate">{folder.name}</span>
          <span className="text-xs text-tertiary shrink-0">
            {folder.file_count} {folder.file_count === 1 ? "file" : "files"}
          </span>
        </div>
      ),
      type: typeLabel === "Mixed" ? (
        <span className="text-sm text-secondary">Mixed</span>
      ) : typeLabel ? (
        <Tag size="sm" color="stone">
          {replaceBucketContent(typeLabel, deceasedUserData)}
        </Tag>
      ) : null,
      uploadDate: folder.latest_upload_date
        ? format(new Date(folder.latest_upload_date), "MMM dd, yyyy")
        : "",
      uploadedBy: folder.created_by_name === "Elayne Team" ? (
        <ElayneTeamBadge />
      ) : (
        <span className="text-sm text-gray-700">
          {folder.created_by_name}
        </span>
      ),
      actions: (
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-stone-100">
                <MoreHorizontal className="h-4 w-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" collisionPadding={16} className="min-w-[200px] sm:min-w-[260px] max-w-[calc(100vw-32px)] p-2">
              <DropdownMenuItem
                onClick={() => onFolderRename?.(folder)}
                className="cursor-pointer h-10 px-3"
              >
                Rename folder
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onFolderDownload?.(folder)}
                className="cursor-pointer h-10 px-3"
              >
                Download all
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onFolderDelete?.(folder)}
                className="cursor-pointer px-3 py-2 h-auto"
              >
                <div className="flex flex-col">
                  <span>Delete folder</span>
                  <span className="text-xs text-secondary">Files move back to the main list</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    };
  });

  const hasDocuments = documentRows.length > 0 || folderRows.length > 0;
  const shouldShowSkeletons = isLoading || (!hasDocuments && documents);
  const data = shouldShowSkeletons ? skeletonRows : [...folderRows, ...documentRows];

  // Distinguish between empty state and no results state
  const isEmptyState = !isLoading && !hasDocuments && !hasSearchOrFilter;
  const isNoResultsState = !isLoading && !hasDocuments && hasSearchOrFilter;

  return (
    <div className="w-full relative flex flex-col">
      <div className="overflow-x-auto">
        <Table
          columns={columns}
          data={data}
          maxHeight="none"
          className={`min-w-[700px] w-full overflow-visible [&_th]:pb-4 [&_th]:bg-white [&_th]:z-10 [&_td]:px-6 [&_td]:!py-4 [&_tr:not(:first-child)]:border-t [&_tr]:border-primary [&_thead]:border-b [&_thead]:border-primary [&_td]:align-middle [&_td]:font-normal`}
          tableHeaderClassName="bg-white sticky top-0 z-10"
          onRowClick={
            shouldShowSkeletons
              ? undefined
              : (_row, index) => {
                  if (index < folderRows.length) {
                    onFolderOpen?.(folders[index].id);
                  } else {
                    handleDocumentClick(documents[index - folderRows.length]);
                  }
                }
          }
        />
      </div>

      {isNoResultsState && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="py-10 px-6 w-full bg-white shadow-[0_-90px_150px_-20px_white,0_90px_150px_-20px_white] flex flex-col items-center justify-center">
            <p className="text-base text-primary font-semibold text-center">No documents found</p>
            <p className="mt-2 text-sm text-secondary max-w-sm text-center text-pretty">
              Try refining your search or adjusting your filters.
            </p>
            {onClearFilters && (
              <Button
                variant="outline"
                size="sm"
                className="mt-6 h-10 px-6"
                onClick={onClearFilters}
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      )}

      {isEmptyState && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="py-10 px-6 w-full bg-white shadow-[0_-90px_150px_-20px_white,0_90px_150px_-20px_white] flex flex-col items-center justify-center">
            <p className="text-base sm:text-xl text-primary font-semibold text-center text-balance">
              Upload your first document
            </p>
            <p className="mt-2 text-sm sm:text-base text-secondary leading-relaxed max-w-md text-center text-pretty">
              We'll extract key insights and guide you step by step through the estate settlement
              process.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-6 h-10 px-6"
              onClick={handleUploadClick}
            >
              Add Document
            </Button>
          </div>
        </div>
      )}

      {/* Upload dialog is rendered and controlled by the parent (DocumentVaultList) */}

      <ConfirmDeleteDialog
        isOpen={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        description={`Are you sure you want to delete "${documentToDelete?.fileName}"? This action cannot be undone.`}
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDeleteDialog
        isOpen={resetDialogOpen}
        onClose={() => {
          setResetDialogOpen(false);
          setDocumentToReset(null);
        }}
        onConfirm={handleConfirmReset}
        title="Reset Extraction"
        description={`This will delete all contacts, assets, debts, beneficiaries, and provenance data extracted from "${documentToReset?.fileName}". The document will return to pending status.`}
        isLoading={isResetting}
      />

      {mobileMoveDoc && (
        <Dialog open={!!mobileMoveDoc} onOpenChange={(open) => { if (!open) setMobileMoveDoc(null); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle className="sr-only">Move to folder</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className="text-xs text-tertiary mb-3">Move to</p>
              <div className="flex flex-col">
                {allFolders
                  .filter((f) => f.id !== mobileMoveDoc.folderId)
                  .map((folder) => (
                    <button
                      key={folder.id}
                      onClick={async () => {
                        await handleMoveToFolder(mobileMoveDoc.id, folder.id);
                        setMobileMoveDoc(null);
                      }}
                      className="px-1 py-2.5 text-left text-sm hover:bg-stone-50 rounded-md"
                    >
                      {folder.name}
                    </button>
                  ))}
                {mobileMoveDoc.folderId && (
                  <button
                    onClick={async () => {
                      await handleMoveToFolder(mobileMoveDoc.id, null);
                      setMobileMoveDoc(null);
                    }}
                    className="px-1 py-2.5 text-left text-sm hover:bg-stone-50 rounded-md"
                  >
                    Main list
                  </button>
                )}
                <div className="border-t border-primary my-1" />
                <button
                  onClick={() => {
                    setMobileMoveDoc(null);
                    onNewFolderAndMove?.(mobileMoveDoc.id);
                  }}
                  className="flex items-center gap-1.5 px-1 py-2.5 text-left text-sm text-brand hover:bg-stone-50 rounded-md"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  <span className="font-medium">New folder</span>
                </button>
              </div>
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}

      {selectedDocument && (
        <DocumentSheet
          isOpen={documentSheetOpen}
          setIsOpen={(open) => {
            if (!open) {
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete("doc");
                  return next;
                },
                { replace: true },
              );
            }
          }}
          documentId={selectedDocument.id}
          documentName={selectedDocument.name}
          documentCreatedAt={selectedDocument.createdAt}
          onDocumentLoaded={handleDocumentLoaded}
        />
      )}
    </div>
  );
};

export default DocumentVaultTable;
